import React, {Component, PropTypes} from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {bindActionCreators} from 'redux';
import {autobind, throttle} from 'core-decorators';
import classNames from 'classnames';
import {connect} from 'store';
import {setWidth} from 'store/ui/actions';
import {isMobile, isLandscape, getViewportHeight, toPX} from 'utils';
import Footer from 'components/footer/footer';
import Search from 'components/search/search';
import Heatmap from 'components/heatmap/heatmap';
import Transcript from 'components/transcript/transcript';
import Captions from 'components/transcript/captions';
import Loading from './loading';

@autobind
export class Root extends Component {
    static propTypes = {
        config: ImmutablePropTypes.map.isRequired,
        compact: PropTypes.bool.isRequired,
        loading: PropTypes.bool.isRequired,
        firstLoading: PropTypes.bool.isRequired,
        error: PropTypes.bool.isRequired,
        results: ImmutablePropTypes.list.isRequired,
        rootWidth: PropTypes.number.isRequired,
        videoNode: PropTypes.object,
        videoWidth: PropTypes.number.isRequired,
        videoHeight: PropTypes.number.isRequired,
        setWidth: PropTypes.func.isRequired,
        scrollIntoView: PropTypes.bool,
    };

    static defaultProps = {
        videoNode: null,
        scrollIntoView: false,
    };

    state = {
        width: '100%',
        height: 'auto',
        videoMargins: {top: 0, right: 0, bottom: 0, left: 0},
        mobile: isMobile(),
        landscape: false,
    };

    componentDidMount() {
        this.updateLayout();
        window.addEventListener('resize', this.handleResize);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return nextProps.config !== this.props.config
            || nextProps.compact !== this.props.compact
            || nextProps.loading !== this.props.loading
            || nextProps.firstLoading !== this.props.firstLoading
            || nextProps.error !== this.props.error
            || nextProps.results !== this.props.results
            || nextProps.rootWidth !== this.props.rootWidth
            || nextProps.videoNode !== this.props.videoNode
            || nextProps.videoWidth !== this.props.videoWidth
            || nextProps.videoHeight !== this.props.videoHeight
            || nextProps.setWidth !== this.props.setWidth
            || nextProps.scrollIntoView !== this.props.scrollIntoView
            || nextState.width !== this.state.width
            || nextState.height !== this.state.height
            || nextState.mobile !== this.state.mobile
            || nextState.landscape !== this.state.landscape;
    }

    componentDidUpdate() {
        this.updateLayout();
        if (this.props.scrollIntoView && this.rootNode && !this.props.loading && !this.scrolled) {
            this.rootNode.scrollIntoView({block: 'start', behavior: 'smooth'});
            this.scrolled = true;
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    setRootNodeRef(node) {
        this.rootNode = node;
    }

    getNode(selector) {
        return this.rootNode.querySelector(selector);
    }

    getVideoMargins() {
        const {videoNode, config} = this.props;
        const keys = ['top', 'right', 'bottom', 'left'];

        if (!window) return keys.reduce((values, key) => ({...values, [key]: 0}), {});

        // Reset top margin before extracting if we have touched it
        if (config.get('searchPosition') === 'above') videoNode.style['margin-top'] = null;

        const style = window.getComputedStyle(this.props.videoNode);
        return keys.reduce((values, key) => {
            const value = style[`margin-${key}`];
            values[key] = (value && Number(value.replace('px', ''))) || 0;
            return values;
        }, {});
    }

    updateLayout() {
        const mobile = isMobile();
        const landscape = mobile && isLandscape();

        const {videoWidth, firstLoading, rootWidth} = this.props;
        const {domContainer, transcriptVisible, captionsVisible, expandHeight} = this.props.config.toJS();

        const width = (mobile || domContainer) ? '100%' : videoWidth;

        let height = 'auto';
        if (!firstLoading) {
            if (domContainer) {
                height = '100%';
            } else if (expandHeight) {
                height = getViewportHeight();
            } else if (mobile) {
                if (landscape) height = this.props.videoHeight;
                else if (transcriptVisible || captionsVisible) height = getViewportHeight() - 40;
            }
        }

        const videoMargins = this.getVideoMargins();

        const next = () => {
            if (rootWidth !== this.rootNode.offsetWidth) this.props.setWidth(this.rootNode.offsetWidth);
            this.updatePosition();
        };

        if (this.state.width !== width ||
            this.state.height !== height ||
            this.state.mobile !== mobile ||
            this.state.landscape !== landscape ||
            this.state.videoMargins !== videoMargins) {
            this.setState({width, height, mobile, landscape, videoMargins}, next);
        } else {
            next();
        }
    }

    updatePosition() {
        if (this.props.config.get('domContainer')) return; // Skip if inside DOM Container

        const {videoNode} = this.props;
        const {videoMargins} = this.state;

        // Make video clickable
        videoNode.style.zIndex = 1;
        videoNode.style.display = 'block';
        videoNode.style.position = 'relative';

        // Compensate margin between video and wrapper
        this.rootNode.style.top = toPX(videoMargins.bottom * -1);
        // Transfer video margins
        this.rootNode.style.marginRight = toPX(videoMargins.right);
        this.rootNode.style.marginLeft = toPX(videoMargins.left);
        this.rootNode.style.marginBottom = toPX(videoMargins.bottom);

        const searchNode = this.getNode('.cielo24-vwrap-search-bar');
        if (!searchNode && this.props.config.get('searchEnabled')) return;

        let rootNodeOffset = videoNode.offsetHeight + videoMargins.bottom;
        if (this.props.config.get('searchPosition') === 'above' && searchNode) {
            const searchHeight = Math.max(40, searchNode.offsetHeight);
            rootNodeOffset += searchHeight;
            // Add space for searchbar while maintaining original margins
            videoNode.style.marginTop = toPX(searchHeight + videoMargins.top);
            // Add space for the video between searchbar and heatmap
            searchNode.style.marginBottom = toPX(videoNode.offsetHeight);
        } else {
            // Add space for the video above the searchbar
            this.rootNode.style.paddingTop = toPX(videoNode.offsetHeight);
        }

        // Offset the wrapper on top of the video
        this.rootNode.style.position = 'absolute';
        this.rootNode.style.top = toPX(rootNodeOffset * -1);
        // // Remove excedent height left in the parent container and apply bottom margin
        this.rootNode.parentNode.style.position = 'relative';
        this.rootNode.parentNode.style.height = toPX(this.rootNode.offsetHeight - rootNodeOffset);
        this.rootNode.parentNode.style.marginBottom = toPX(videoMargins.bottom);
    }

    @throttle(300, {leading: false})
    handleResize() {
        this.updateLayout();
    }

    render() {
        const {loading, firstLoading, error} = this.props;
        const {landscape} = this.state;
        const config = this.props.config.toJS();
        const {
            onlySeo,
            shareEnabled,
            printEnabled,
            downloadEnabled,
            heatmapVisible,
            searchEnabled,
            captionsVisible,
            transcriptVisible,
            captionsFontSize,
            transcriptFontSize,
        } = config;

        const rootStyle = {
            width: this.state.width,
            height: this.state.height,
        };

        const rootClassName = classNames('cielo24-vwrap-root', {
            'cielo24-vwrap-loading': firstLoading,
            'cielo24-vwrap-mobile': this.state.mobile,
            'cielo24-vwrap-stretch': this.state.mobile || config.expandHeight || config.domContainer,
            'cielo24-vwrap-compact': this.props.compact,
            'cielo24-vwrap-dom-container': config.domContainer,
            'cielo24-vwrap-no-bold': config.noBold,
            'cielo24-vwrap-rtl': config.rtl,
        });

        return (
            <div ref={this.setRootNodeRef} className={rootClassName} style={rootStyle}>
                <div className='cielo24-vwrap-container'>
                    {landscape && !firstLoading && (
                        <div className='cielo24-vwrap-landscape'>
                            Please rotate your phone to interact with this video
                        </div>
                    )}
                    {!onlySeo && !error && !firstLoading && searchEnabled && <Search />}
                    {!onlySeo && !error && !firstLoading && heatmapVisible && <Heatmap />}
                    {!onlySeo && !error && loading && <Loading />}
                    {!onlySeo && !error && !loading && captionsVisible && (
                        <Captions fontSize={captionsFontSize} />
                    )}
                    {!onlySeo && !error && !loading && transcriptVisible && (
                        <Transcript fontSize={transcriptFontSize} />
                    )}
                    {!onlySeo && !error && (
                        <Footer
                            loading={firstLoading}
                            share={shareEnabled}
                            print={printEnabled}
                            download={downloadEnabled}
                            hideLogo={config.hideCielo24Logo}
                            disableLink={config.disableCielo24Link}
                            clientLogoImage={config.clientLogoImage}
                            clientLogoHref={config.clientLogoHref}
                            sponsorLogoImage={config.sponsorLogoImage}
                            sponsorLogoHref={config.sponsorLogoHref}
                        />
                    )}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    config: state.config,
    compact: state.ui.get('compact'),
    loading: state.elementList.get('loading'),
    firstLoading: state.elementList.get('loading') && state.elementList.get('cache').keySeq().size === 0,
    error: state.elementList.get('error'),
    results: state.search.get('results'),
    rootWidth: state.ui.get('width'),
    videoWidth: state.video.get('width'),
    videoHeight: state.video.get('height'),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({setWidth}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Root);



// WEBPACK FOOTER //
// ./scripts/components/layout/root.js